import { formatDistanceToNowStrict } from "date-fns";
import { AlertCircleIcon } from "lucide-react";
import { useId, useMemo } from "react";

import type { ISkillVersion } from "@/api/skills";
import { cn } from "@/lib/utils";

import { sortSkillVersionsByRecency } from "./skill-version-order";

export const HISTORY_VISIBLE_LIMIT = 5;

export interface SkillVersionHistoryProps {
    versions: ISkillVersion[] | undefined;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    onSelectVersion: (versionId: number) => void;
    onViewAll: () => void;
}

const GROUP_LABEL_CLASS =
    "text-muted-foreground flex items-center justify-between px-2 text-[11px] font-medium tracking-wider uppercase";

const ROW_CLASS =
    "focus-visible:ring-ring/50 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors outline-none focus-visible:ring-3";

const ROW_IDLE_CLASS =
    "text-muted-foreground hover:bg-muted hover:text-foreground";

const MESSAGE_CLASS = "text-muted-foreground px-2 py-1.5 text-xs";

export function SkillVersionHistory({
    versions,
    isLoading,
    isError,
    onRetry,
    onSelectVersion,
    onViewAll,
}: SkillVersionHistoryProps) {
    const headingId = useId();

    const ordered = useMemo(
        () => (versions ? sortSkillVersionsByRecency(versions) : []),
        [versions]
    );

    return (
        <div className="shrink-0 space-y-1 border-t px-2 py-3">
            <h2 id={headingId} className={GROUP_LABEL_CLASS}>
                <span>History</span>
                {!isError && versions && (
                    <span className="tabular-nums">{versions.length}</span>
                )}
            </h2>

            {isError ? (
                <div
                    role="status"
                    className="bg-destructive/5 text-destructive flex items-center gap-2 rounded-md px-2 py-2 text-xs"
                >
                    <AlertCircleIcon
                        aria-hidden="true"
                        className="size-3.5 shrink-0"
                    />
                    <span className="flex-1">
                        History unavailable. Editing and saving are unaffected.
                    </span>
                    <button
                        type="button"
                        onClick={onRetry}
                        className="font-medium underline-offset-2 hover:underline"
                    >
                        Retry
                    </button>
                </div>
            ) : isLoading || !versions ? (
                <p className={MESSAGE_CLASS}>Loading history…</p>
            ) : ordered.length === 0 ? (
                <p className={MESSAGE_CLASS}>No versions yet.</p>
            ) : (
                <div
                    role="group"
                    aria-labelledby={headingId}
                    className="space-y-0.5"
                >
                    {ordered.slice(0, HISTORY_VISIBLE_LIMIT).map((version) => {
                        const publishedAgo = formatDistanceToNowStrict(
                            version.published_at,
                            { addSuffix: true }
                        );
                        const publisherName = `${version.publisher.first_name} ${version.publisher.last_name}`;
                        return (
                            <button
                                key={version.id}
                                type="button"
                                onClick={() => onSelectVersion(version.id)}
                                aria-label={`v${version.version_number}, ${publishedAgo}, published by ${publisherName}`}
                                className={cn(
                                    ROW_CLASS,
                                    "flex-col items-stretch gap-0",
                                    ROW_IDLE_CLASS
                                )}
                            >
                                <span className="flex w-full items-center gap-2">
                                    <span className="min-w-0 flex-1 truncate">
                                        v{version.version_number}
                                    </span>
                                    <span className="shrink-0 text-[11px]">
                                        {publishedAgo}
                                    </span>
                                </span>
                                <span className="text-muted-foreground/70 w-full truncate text-left text-[11px]">
                                    {publisherName}
                                </span>
                            </button>
                        );
                    })}

                    <button
                        type="button"
                        onClick={onViewAll}
                        className={cn(ROW_CLASS, ROW_IDLE_CLASS)}
                    >
                        View all history
                    </button>
                </div>
            )}
        </div>
    );
}
